// dashboard/frontend/src/components/Campaign/channels/BaseDraftRow.js

import React, { useState } from 'react';
import {
    TableRow,
    TableCell,
    Chip,
    Box,
    Collapse,
    CircularProgress,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Email as EmailIcon,
} from '@mui/icons-material';

/**
 * Extract username from Instagram URL
 * @param {string} url - Instagram profile URL
 * @returns {string} - Username or original URL if extraction fails
 */
const extractInstagramUsername = (url) => {
    if (!url) return '';
    
    try {
        // Handle various IG URL formats:
        // https://instagram.com/username
        // https://www.instagram.com/username/
        // instagram.com/username
        const match = url.match(/instagram\.com\/([^/?#]+)/i);
        if (match && match[1]) {
            return '@' + match[1];
        }
    } catch (e) {
        console.error('Error extracting IG username:', e);
    }
    
    // Fallback to original URL
    return url;
};

const AnimatedTextField = styled(TextField)(({ theme, updating }) => ({
    '& .MuiInputBase-root': {
        transition: updating ? 'background-color 0.5s ease' : 'none',
        backgroundColor: updating ? theme.palette.action.hover : 'transparent',
    }
}));

/**
 * Base component for draft rows - shared logic across all channels
 * This handles the display and expansion, while actions are channel-specific
 */
const BaseDraftRow = ({ 
    contact, 
    draft, 
    channelConfig,
    actions,  // Function that returns action buttons
}) => {
    const [expanded, setExpanded] = useState(false);

    const getContactChannelInfo = () => {
        const value = contact[channelConfig.urlField];
        
        if (!value) return 'N/A';
        
        // For Instagram, show just the username
        if (channelConfig.urlField === 'instagram_url') {
            return extractInstagramUsername(value);
        }
        
        // For other channels, show the full value
        return value;
    };

    const channelInfo = getContactChannelInfo();
    const hasChannelInfo = channelInfo && channelInfo !== 'N/A';

    const getDraftStatus = () => {
        if (!draft) {
            return { 
                color: 'default', 
                label: 'Not Generated', 
                icon: null 
            };
        }

        // Show regenerating state
        if (draft._regenerating) {
            return {
                color: 'info',
                label: 'Regenerating...',
                icon: <CircularProgress size={14} />
            };
        }

        if (draft.status === 'sent') {
            return { 
                color: 'success', 
                label: channelConfig.statusLabels.sent || 'Sent', 
                icon: <CheckCircleIcon fontSize="small" /> 
            };
        }

        if (draft.status === 'error') {
            return { 
                color: 'error', 
                label: channelConfig.statusLabels.error || 'Error', 
                icon: <ErrorIcon fontSize="small" /> 
            };
        }

        // Special handling for email with Gmail draft
        if (channelConfig.urlField === 'email' && draft.gmail_draft_id) {
            return { 
                color: 'primary', 
                label: 'In Gmail', 
                icon: <EmailIcon fontSize="small" /> 
            };
        }

        return { 
            color: 'warning', 
            label: channelConfig.statusLabels.draft || 'Draft', 
            icon: null 
        };
    };

    const status = getDraftStatus();

    return (
        <>
            <TableRow>
                <TableCell>
                    {contact.first_name} {contact.last_name}
                    <Typography variant="caption" display="block" color="textSecondary">
                        {contact.company_name}
                    </Typography>
                </TableCell>
                <TableCell>
                    {hasChannelInfo ? (
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                maxWidth: 200, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {channelInfo}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="error">
                            No {channelConfig.name} info
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        icon={status.icon}
                    />
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Render channel-specific actions */}
                        {actions && actions({ 
                            draft, 
                            contact, 
                            hasChannelInfo, 
                            expanded, 
                            setExpanded 
                        })}
                    </Box>
                </TableCell>
            </TableRow>
            
            {/* Expandable content row */}
            {draft && (
                <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0 }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                {draft.subject && channelConfig.hasSubject && (
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        value={draft.subject}
                                        margin="dense"
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                        sx={{ mb: 1 }}
                                    />
                                )}
                                <AnimatedTextField
                                    fullWidth
                                    label="Message Content"
                                    value={draft.message || ''}
                                    multiline
                                    rows={6}
                                    margin="dense"
                                    variant="outlined"
                                    InputProps={{ readOnly: true }}
                                    updating={draft._justUpdated ? 1 : 0}
                                />
                                {draft.sent_at && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Sent: {new Date(draft.sent_at).toLocaleString()}
                                    </Typography>
                                )}
                                {draft.error_message && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        Error: {draft.error_message}
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

export default BaseDraftRow;